import { ActionIcon, Box, Group, Tooltip } from '@mantine/core';
import { IconStar, IconStarFilled, IconTrash } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import type { ReactNode } from 'react';

import { BIC_MASK, IBAN_MASK, MaskedTextInput } from './MaskedTextInput';

type BankAccountRowProps = {
  ibanValue: string;
  bicValue: string;
  onIbanChange?: (value: string) => void;
  onBicChange?: (value: string) => void;
  ibanError?: ReactNode;
  bicError?: ReactNode;
  disabled?: boolean;
  isDefault: boolean;
  onSetDefault?: () => void;
  onRemove?: () => void;
  onArchive?: () => void;
};

export const BankAccountRow = ({
  ibanValue,
  bicValue,
  onIbanChange,
  onBicChange,
  ibanError,
  bicError,
  disabled,
  isDefault,
  onSetDefault,
  onRemove,
  onArchive,
}: BankAccountRowProps) => {
  const { t } = useTranslation();

  const starLabel = isDefault
    ? t(i18nKeys.SETTINGS.COMPANY.DEFAULT_BANK_ACCOUNT)
    : t(i18nKeys.SETTINGS.COMPANY.SET_DEFAULT_BANK_ACCOUNT);

  return (
    <Group align="end" wrap="nowrap" gap="sm">
      <Tooltip label={starLabel}>
        <ActionIcon
          variant="subtle"
          color="yellow"
          size={36}
          disabled={!onSetDefault}
          aria-label={starLabel}
          onClick={onSetDefault}
        >
          {isDefault ? <IconStarFilled size={18} /> : <IconStar size={18} />}
        </ActionIcon>
      </Tooltip>
      <MaskedTextInput
        flex={2}
        label="IBAN"
        placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.IBAN)}
        disabled={disabled}
        error={ibanError}
        value={ibanValue}
        onChange={onIbanChange}
        maskConfig={IBAN_MASK}
      />
      <MaskedTextInput
        flex={1}
        label="BIC"
        placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.BIC)}
        disabled={disabled}
        error={bicError}
        value={bicValue}
        onChange={onBicChange}
        maskConfig={BIC_MASK}
      />
      {onArchive && (
        <ActionIcon
          variant="subtle"
          color="red"
          size={36}
          aria-label={t(i18nKeys.SETTINGS.COMPANY.ARCHIVE_BANK_ACCOUNT)}
          onClick={onArchive}
        >
          <IconTrash size={18} />
        </ActionIcon>
      )}
      {onRemove ? (
        <ActionIcon
          variant="subtle"
          color="red"
          size={36}
          aria-label={t(i18nKeys.DELETE)}
          onClick={onRemove}
        >
          <IconTrash size={18} />
        </ActionIcon>
      ) : (
        !onArchive && <Box w={36} style={{ flexShrink: 0 }} />
      )}
    </Group>
  );
};
